/**
 * Bouwt een schema.org `Service` JSON-LD script-tag voor gebruik in
 * TanStack Start `head().scripts`. De `provider` verwijst via `@id` naar de
 * bestaande LocalBusiness-entiteit uit `__root.tsx`, zodat er geen dubbele
 * organisatie-entiteit ontstaat in Rich Results.
 */
export type ServiceJsonLdInput = {
  /** URL-pad van de servicepagina, bv. "/elektrotechniek" */
  path: string;
  /** Naam van de dienst */
  name: string;
  /** Korte omschrijving (max ~300 tekens aanbevolen) */
  description: string;
  /** Bv. "Elektrotechniek", "Beveiligingstechniek", "Netwerktechniek" */
  serviceType: string;
  /** Sub-diensten (bv. "Camerasystemen", "Groepenkasten") */
  offerCatalog: string[];
  /**
   * Optioneel: overschrijft `areaServed`. Geef een string (stadsnaam) door voor
   * een `City`, of een volledig schema.org-object voor complexere gevallen.
   * Default: "Nederland" (Country).
   */
  areaServed?: string | Record<string, unknown>;
};

const SITE = "https://elvion-group.nl";
const BUSINESS_ID = `${SITE}/#localbusiness`;

export function serviceJsonLd(input: ServiceJsonLdInput) {
  const url = `${SITE}${input.path}`;

  const data = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: input.name,
    description: input.description,
    serviceType: input.serviceType,
    url,
    provider: {
      "@type": "LocalBusiness",
      "@id": BUSINESS_ID,
      name: "Elvion Group Nederland",
      url: SITE,
    },
    areaServed:
      typeof input.areaServed === "string"
        ? { "@type": "City", name: input.areaServed, containedInPlace: { "@type": "Country", name: "Nederland" } }
        : input.areaServed ?? { "@type": "Country", name: "Nederland" },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: url,
      servicePhone: "+31852501112",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: input.name,
      itemListElement: input.offerCatalog.map((item) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: item },
      })),
    },
  };

  return {
    type: "application/ld+json" as const,
    children: JSON.stringify(data),
  };
}
